/**
 * Projects Service Hooks
 * React hooks for accessing project management services
 */
import { useCCMS } from '../useCCMS';
/**
 * Hook to access all projects-related services
 */
export function useProjectsServices() {
    const { services } = useCCMS();
    if (!services?.projects) {
        throw new Error('Projects services not available. Ensure CCMSProvider is configured with projects services.');
    }
    return services.projects;
}
/**
 * Hook to access ProjectService
 */
export function useProjectService() {
    return useProjectsServices().project;
}
/**
 * Hook to access ProjectStatusService
 */
export function useProjectStatusService() {
    return useProjectsServices().projectStatus;
}
/**
 * Hook to access ProjectTypeService
 */
export function useProjectTypeService() {
    return useProjectsServices().projectType;
}
/**
 * Hook to access ProjectAuditLogService
 */
export function useProjectAuditLogService() {
    return useProjectsServices().projectAuditLog;
}
/**
 * Hook to access ProjectMessageService
 */
export function useProjectMessageService() {
    return useProjectsServices().projectMessage;
}
/**
 * Hook to access ProjectMilestoneService
 */
export function useProjectMilestoneService() {
    return useProjectsServices().projectMilestone;
}
/**
 * Hook to access ProjectMilestoneTaskService
 */
export function useProjectMilestoneTaskService() {
    return useProjectsServices().projectMilestoneTask;
}
/**
 * Hook to access ProjectRiskService
 */
export function useProjectRiskService() {
    return useProjectsServices().projectRisk;
}
/**
 * Hook to access ProjectTeamMemberService
 */
export function useProjectTeamMemberService() {
    return useProjectsServices().projectTeamMember;
}
/**
 * Hook to access TaskTimeLogService
 */
export function useTaskTimeLogService() {
    return useProjectsServices().taskTimeLog;
}
/**
 * Hook to access ProjectResourceService
 */
export function useProjectResourceService() {
    return useProjectsServices().projectResource;
}
/**
 * Hook to access ProjectDocumentService
 */
export function useProjectDocumentService() {
    return useProjectsServices().projectDocument;
}
/**
 * Hook to access PlannedBudgetItemService
 */
export function usePlannedBudgetItemService() {
    return useProjectsServices().plannedBudgetItem;
}
/**
 * Hook to access TaskDependencyService
 */
export function useTaskDependencyService() {
    return useProjectsServices().taskDependency;
}
/**
 * Hook to access ResourceTypeService
 */
export function useResourceTypeService() {
    return useProjectsServices().resourceType;
}
/**
 * Hook to access ProjectRoleService
 */
export function useProjectRoleService() {
    return useProjectsServices().projectRole;
}
/**
 * Hook to access BudgetLineItemService
 */
export function useBudgetLineItemService() {
    return useProjectsServices().budgetLineItem;
}
/**
 * Hook to access ProjectSettingsService (domain settings + per-type feature gates)
 */
export function useProjectSettingsService() {
    return useProjectsServices().projectSettings;
}
/**
 * Hook to access ProjectScheduleService (Stage 6b CPM critical-path scheduling)
 */
export function useProjectScheduleService() {
    return useProjectsServices().projectSchedule;
}
/**
 * Hook to access ProjectPortfolioService (Stage 8 - Portfolios/Programs + rollup)
 */
export function useProjectPortfolioService() {
    return useProjectsServices().projectPortfolio;
}
/**
 * Hook to access ProjectTemplateService (Stage 7 templates + instantiation)
 */
export function useProjectTemplateService() {
    return useProjectsServices().projectTemplate;
}
/**
 * Hook to access ProjectCustomFieldService (Stage 7 per-type custom fields)
 */
export function useProjectCustomFieldService() {
    return useProjectsServices().projectCustomField;
}
/**
 * Hook to access ProjectBaselineService (Stage 6 scheduling baselines: capture/compare)
 */
export function useProjectBaselineService() {
    return useProjectsServices().projectBaseline;
}
/**
 * Hook to access ProjectOriginService
 */
export function useProjectOriginService() {
    return useProjectsServices().projectOrigin;
}
//# sourceMappingURL=useProjectsServices.js.map